import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Image,
  Text,
  keyframes,
  Skeleton,
  SkeletonText,
} from '@chakra-ui/react'
import AvatarHeading from '@assets/images/roadmap/avatar-heading.png'
import BackgroundPatternDecorative from '@assets/images/roadmap/background-pattern-decorative.png'
import Ellipse from '@assets/images/roadmap/ellipse.png'
import {ReactComponent as ArrowRight} from '@assets/svgs/roadmap/arrow_right.svg'
import {ReactComponent as Target} from '@assets/svgs/roadmap/target.svg'
import {ReactComponent as Layers_three} from '@assets/svgs/roadmap/layers_three.svg'
import {useState, useEffect} from 'react'
import {isLoggedIn, trackingEvent} from '@utils'
import {useNavigate} from 'react-router-dom'
import {EVENT_NAME, PATH} from '@utils/Const'
import mapRequest from '@apis/map'
import {useDispatch, useSelector} from 'react-redux'
import {
  updateEntryPointShowUpgradeModal,
  updateIsShowUpgradeModal,
} from '@redux/map/mapSlice'
import {updateCurrentCourseId} from '@redux/shared/shared'

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`

const HeaderSkeleton = () => (
  <Box
    bg="#7A5AF8"
    borderRadius="16px"
    p={'32px'}
    mx="auto"
    boxShadow="md"
    position={'relative'}>
    <Skeleton height="30px" width="200px" mb={2} />
    <SkeletonText mt="4" noOfLines={3} spacing="4" skeletonHeight="2" />
    <Flex gap={'40px'} my={'32px'}>
      <Skeleton height="8px" width="200px" borderRadius="full" />
      <Skeleton height="20px" width="100px" />
      <Skeleton height="20px" width="100px" />
    </Flex>
    <Button
      bg="white"
      color="#344054"
      fontWeight="bold"
      py={3}
      px={6}
      borderRadius="full"
      transition="all 0.3s"
      display="flex"
      alignItems="center"
      justifyContent="center">
      <Skeleton height="16px" width="100px" />
      <Icon as={ArrowRight} ml={2} w={'20px'} h={'20px'} />
    </Button>
  </Box>
)

const Header = ({roadmapData}: {roadmapData: any}) => {
  const {dataFeedbackOverview} = useSelector((state: any) => state.shared)
  const [showFixedHeader, setShowFixedHeader] = useState(false)
  const [currentMaxTask, setCurrentMaxTask] = useState<any>(null)
  const [isNewBeginner, setIsNewBeginner] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  useEffect(() => {
    if (!roadmapData) return
    const completedTaskExists = hasCompletedTask(roadmapData)
    setIsNewBeginner(!completedTaskExists)
  }, [roadmapData])

  useEffect(() => {
    if (roadmapData?.body?.max_task_id) {
      const maxTaskId = roadmapData.body.max_task_id
      let foundTask = null
      roadmapData.body.phases.forEach((phase: any) => {
        phase.tasks_sections.forEach((section: any) => {
          section.tasks.forEach((task: any) => {
            if (task.id === maxTaskId) {
              foundTask = task
            }
          })
        })
      })
      setCurrentMaxTask(foundTask)
    }
  }, [roadmapData])

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 250) {
        setShowFixedHeader(true)
      } else {
        setShowFixedHeader(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleClickTask = () => {
    trackingEvent(EVENT_NAME.MAP_CONTINUE_BUTTON_CLICK)

    if (isLoggedIn()) {
      updateCurrentTask()
    }

    if (!roadmapData?.is_course_available) {
      alert('Bạn chưa được mở lộ trình hiện tại')
      return
    }

    if (isLoggedIn() && currentMaxTask.is_lock) {
      dispatch(updateIsShowUpgradeModal(true))
      dispatch(updateEntryPointShowUpgradeModal('roadmap_page'))
      return
    }

    if (!isLoggedIn() && roadmapData?.heading?.is_complete_first_task) {
      navigate(PATH.LOGIN)
      return
    }

    switch (currentMaxTask.type) {
      case 'LESSON':
      case 'LESSON_PART_ONE':
      case 'LESSON_PART_TWO':
        navigate(PATH.CONVERSATION, {
          state: {
            entry_point: 'ROADMAP',
            lesson_id: currentMaxTask.input_params.lesson_id,
            lesson_name: currentMaxTask.input_params.lesson_name,
            feature_name: currentMaxTask.type,
          },
        })
        break
      case 'FAST_TRACK':
        navigate(PATH.LEARN_FAST_TRACK, {
          state: {
            entry_point: 'ROADMAP',
            lesson_id: currentMaxTask.input_params.lesson_id,
            lesson_name: currentMaxTask.input_params.lesson_name,
            output_band: currentMaxTask.input_params.output_band,
          },
        })
        break
      case 'VOCABULARY':
        navigate(PATH.LEARN_VOCABULARY, {
          state: {
            entry_point: 'ROADMAP',
            setId: currentMaxTask.input_params.set_id,
          },
        })
        break
      case 'MOCK_TEST':
      case 'DEMO_TEST':
      case 'REAL_TEST':
      case 'MOCK_TEST_PART_ONE':
      case 'MOCK_TEST_PART_TWO':
      case 'REAL_TEST_PART_ONE':
      case 'REAL_TEST_PART_TWO':
        navigate(PATH.EXAM_ROOM, {
          state: {
            entry_point: 'ROADMAP',
            testId: currentMaxTask.input_params.test_id,
            type: currentMaxTask.input_params.type,
          },
        })
        break
      case 'IPA':
        navigate(PATH.GYM_PRONOUNCE, {
          state: {
            entry_point: 'ROADMAP',
            task_id: currentMaxTask.task_id,
            lesson_id: currentMaxTask.input_params.lesson_id,
            lesson_name: currentMaxTask.input_params.lesson_name,
            type: currentMaxTask.input_params.ipa_lesson_detail_type,
          },
        })
        break
      case 'LESSON_PART_TWO_NEW':
        navigate(PATH.LEARN_PART_TWO, {
          state: {
            entry_point: 'ROADMAP',
            task_id: currentMaxTask.task_id,
            lesson_id: currentMaxTask.input_params.lesson_id,
            task_type: currentMaxTask.type,
          },
        })
        break
      default:
        alert('Chưa có dữ liệu')
        break
    }
  }

  const updateCurrentTask = () => {
    try {
      mapRequest.updateCurrentTask({
        task_id: currentMaxTask?.id,
      })
    } catch (error) {}
  }

  const hasCompletedTask = (roadmapData: any) => {
    for (const phase of roadmapData.body.phases) {
      for (const section of phase.tasks_sections) {
        for (const task of section.tasks) {
          if (task.is_completed) {
            return true
          }
        }
      }
    }
    return false
  }

  const handleChangeCourse = () => {
    dispatch(updateCurrentCourseId(roadmapData?.heading?.next_course_id))
  }

  const renderHeaderFixed = () => {
    return (
      <Box
        className="fixed-header"
        borderRadius="0 0 16px 16px"
        position={{base: 'fixed', md: 'sticky'}}
        top="0"
        left="0"
        right="0"
        bg="#7A5AF8"
        p={{base: '16px', md: '32px'}}
        boxShadow="md"
        zIndex="900"
        animation={`${slideDown} 0.3s ease-in-out`}>
        <Heading
          as="h2"
          fontSize={{base: '20px', md: '30px'}}
          fontWeight="600"
          mb={2}
          color="white">
          {roadmapData?.heading?.name}
        </Heading>
        <Flex gap={{base: '16px', md: '40px'}} flexWrap={'wrap'}>
          <Flex alignItems="center" gap={'12px'}>
            <Box
              w={{base: '100px', md: '200px'}}
              bg="purple.200"
              h="8px"
              borderRadius="full">
              <Box
                bg="white"
                h="8px"
                borderRadius="full"
                w={`${Math.round(
                  (roadmapData?.heading?.number_of_completed_tasks /
                    roadmapData?.heading?.number_of_tasks) *
                    100,
                )}%`}
              />
            </Box>
            <Text fontSize="12px" fontWeight="medium" color="white">
              {Math.round(
                (roadmapData?.heading?.number_of_completed_tasks /
                  roadmapData?.heading?.number_of_tasks) *
                  100,
              )}
              % hoàn thành
            </Text>
          </Flex>
          <Flex align="center">
            <Icon as={Target} color="white" mr={2} stroke={'#D0D5DD'} />
            <Text fontSize="12px" color="white">
              Mục tiêu {roadmapData?.heading?.output_band_score.toFixed(1)}
            </Text>
          </Flex>
          <Flex align="center">
            <Icon as={Layers_three} color="white" mr={2} stroke={'#D0D5DD'} />
            <Text fontSize="12px" color="white">
              {roadmapData?.heading?.number_of_tasks} bài học
            </Text>
          </Flex>
          {roadmapData?.heading?.number_of_tasks ===
          roadmapData?.heading?.number_of_completed_tasks ? (
            <Button
              display={roadmapData?.heading?.next_course_id ? 'flex' : 'none'}
              ml="auto"
              bg="white"
              color="#344054"
              fontWeight="bold"
              py={3}
              px={6}
              borderRadius="full"
              transition="all 0.3s"
              alignItems="center"
              justifyContent="center"
              onClick={handleChangeCourse}>
              <Text fontSize="16px" fontWeight="600">
                Chuyển lộ trình tiếp theo
              </Text>
              <Icon as={ArrowRight} ml={2} w={'20px'} h={'20px'} />
            </Button>
          ) : (
            <Button
              ml="auto"
              bg="white"
              color="#344054"
              fontWeight="bold"
              py={2}
              px={4}
              borderRadius="full"
              transition="all 0.3s"
              display="flex"
              alignItems="center"
              justifyContent="center"
              onClick={handleClickTask}>
              <Text fontSize="14px" fontWeight="600">
                {isNewBeginner ? 'Bắt đầu bài học' : 'Tiếp tục bài học'}
              </Text>
              <Icon as={ArrowRight} ml={2} w={'16px'} h={'16px'} />
            </Button>
          )}
        </Flex>
      </Box>
    )
  }

  if (!roadmapData) {
    return <HeaderSkeleton />
  }

  return (
    <>
      {showFixedHeader && renderHeaderFixed()}
      <Box
        bg="#7A5AF8"
        borderRadius="16px"
        p={{base: '16px', md: '32px'}}
        mx="auto"
        boxShadow="md"
        position={'relative'}>
        <Image
          src={AvatarHeading}
          alt="Avatar"
          position={'absolute'}
          bottom={'0'}
          right={'0'}
          zIndex={800}
          display={{base: 'none', md: 'block'}}
        />
        <Image
          src={BackgroundPatternDecorative}
          alt="BackgroundPatternDecorative"
          position={'absolute'}
          bottom={'0'}
          right={'0'}
          h={'100%'}
          display={{base: 'none', md: 'block'}}
        />
        <Image
          src={Ellipse}
          alt="Ellipse"
          position={'absolute'}
          bottom={'0'}
          right={'0'}
          h={'100%'}
          display={{base: 'none', md: 'block'}}
        />
        <Heading
          as="h2"
          fontSize={{base: '20px', md: '30px'}}
          fontWeight="600"
          mb={2}
          color="white">
          {roadmapData?.heading?.name}
        </Heading>
        <Text
          fontSize={{base: '12px', md: '14px'}}
          color="white"
          mb={{base: 2, md: 4}}
          maxW={'500px'}>
          {roadmapData?.heading?.description}
        </Text>

        <Flex
          gap={{base: '16px', md: '40px'}}
          my={{base: '16px', md: '32px'}}
          flexWrap={'wrap'}>
          <Flex alignItems="center" gap={'12px'}>
            <Box
              w={{base: '100px', md: '200px'}}
              bg="purple.200"
              h="8px"
              borderRadius="full">
              <Box
                bg="white"
                h="8px"
                borderRadius="full"
                w={`${Math.round(
                  (roadmapData?.heading?.number_of_completed_tasks /
                    roadmapData?.heading?.number_of_tasks) *
                    100,
                )}%`}
              />
            </Box>
            <Text
              fontSize="12px"
              fontWeight="medium"
              color="white"
              style={{
                textWrap: 'nowrap',
              }}>
              {Math.round(
                (roadmapData?.heading?.number_of_completed_tasks /
                  roadmapData?.heading?.number_of_tasks) *
                  100,
              )}
              % hoàn thành
            </Text>
          </Flex>
          <Flex align="center">
            <Icon as={Target} color="white" mr={2} stroke={'#D0D5DD'} />
            <Text fontSize="12px" color="white">
              Mục tiêu {roadmapData?.heading?.output_band_score.toFixed(1)}
            </Text>
          </Flex>
          <Flex align="center">
            <Icon as={Layers_three} color="white" mr={2} stroke={'#D0D5DD'} />
            <Text fontSize="12px" color="white">
              {roadmapData?.heading?.number_of_tasks} bài học
            </Text>
          </Flex>
        </Flex>

        {roadmapData?.heading?.number_of_tasks ===
        roadmapData?.heading?.number_of_completed_tasks ? (
          <Flex gap={'24px'} alignItems={'center'}>
            <Text fontWeight={'600'} color={'white'}>
              Bạn đã học xong lộ trình này rồi!
            </Text>
            <Button
              display={roadmapData?.heading?.next_course_id ? 'flex' : 'none'}
              bg="white"
              color="#344054"
              fontWeight="bold"
              py={3}
              px={6}
              borderRadius="full"
              transition="all 0.3s"
              alignItems="center"
              justifyContent="center"
              onClick={handleChangeCourse}>
              <Text fontSize="16px" fontWeight="600">
                Chuyển lộ trình tiếp theo
              </Text>
              <Icon as={ArrowRight} ml={2} w={'20px'} h={'20px'} />
            </Button>
          </Flex>
        ) : (
          <Button
            bg="white"
            color="#344054"
            fontWeight="bold"
            py={3}
            px={6}
            borderRadius="full"
            transition="all 0.3s"
            display="flex"
            alignItems="center"
            justifyContent="center"
            onClick={handleClickTask}>
            <Text fontSize="16px" fontWeight="600">
              {isNewBeginner ? 'Bắt đầu học' : 'Tiếp tục bài học'}
            </Text>
            <Icon as={ArrowRight} ml={2} w={'20px'} h={'20px'} />
          </Button>
        )}
      </Box>
    </>
  )
}

export default Header
